/**
 * Amenity Drawer Component
 */
import React from 'react';
import {
  Box,
  Drawer,
  Typography,
  Button,
  Chip,
  Checkbox,
  FormControlLabel,
  IconButton,
  CircularProgress,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getBookingDrawerPanelSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  getBookingDrawerAuditButtonSx,
  getBookingDrawerInputBg,
  getBookingDrawerDividerColor,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import CloseIcon from '@mui/icons-material/Close';

const THEME_COLOR = '#4ce1b6';

export const AmenityDrawer = ({
  open,
  category,
  amenities,
  selectedAmenityIds,
  isDark,
  saving,
  onClose,
  onToggleAmenity,
  onSave,
}) => {
  const drawerTheme = getBookingDrawerTheme(Boolean(isDark));
  const selectedCount = selectedAmenityIds.length;
  const countText = selectedCount === 1 ? '1 Amenity Selected' : `${selectedCount} Amenities Selected`;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 600 },
          backgroundColor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Fixed Header */}
      <Box
        sx={{
          p: 1.5,
          borderBottom: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
          flexShrink: 0,
          backgroundColor: 'background.paper',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Manage Property Amenities
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: isDark ? drawerTheme.subtleText : '#666', p: 0.5 }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >

        {category && (
          <>
            {/* Category Header */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                {category.label}
              </Typography>
              <Chip
                label={countText}
                size="small"
                sx={{
                  backgroundColor: isDark ? 'rgba(76, 225, 182, 0.2)' : 'rgba(76, 225, 182, 0.1)',
                  color: THEME_COLOR,
                  fontWeight: 500,
                }}
              />
            </Box>

            {/* Amenities List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {amenities.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                  No amenities available
                </Typography>
              ) : (
                <List>
                  {amenities.map((amenity, index) => {
                    const isSelected = selectedAmenityIds.includes(amenity.id);
                    return (
                      <React.Fragment key={amenity.id}>
                        <ListItem
                          sx={{
                            px: 0,
                            py: 1,
                            '&:hover': {
                              backgroundColor: isDark ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                            },
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={isSelected}
                                onChange={() => onToggleAmenity(amenity.id)}
                                sx={{
                                  color: THEME_COLOR,
                                  '&.Mui-checked': {
                                    color: THEME_COLOR,
                                  },
                                }}
                              />
                            }
                            label={amenity.name}
                            sx={{
                              flex: 1,
                              '& .MuiFormControlLabel-label': {
                                color: 'text.primary',
                              },
                            }}
                          />
                        </ListItem>
                        {index < amenities.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </Box>
          </>
        )}
      </Box>

      {/* Fixed Footer */}
      {category && (
        <Box
          sx={{
            p: 1.5,
            borderTop: `2px solid ${isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0'}`,
            flexShrink: 0,
            backgroundColor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={onClose} fullWidth sx={{
              borderColor: isDark ? drawerTheme.surfaceBorderStrong : '#e0e0e0',
              color: isDark ? drawerTheme.text : '#666',
              '&:hover': {
                borderColor: THEME_COLOR,
                backgroundColor: isDark ? 'rgba(76, 225, 182, 0.1)' : 'rgba(76, 225, 182, 0.05)',
                color: THEME_COLOR,
              },
            }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              onClick={onSave}
              disabled={saving}
              sx={{
                backgroundColor: THEME_COLOR,
                color: '#000',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: THEME_COLOR,
                  opacity: 0.9,
                  boxShadow: `0 4px 12px ${isDark ? 'rgba(76, 225, 182, 0.4)' : 'rgba(76, 225, 182, 0.3)'}`,
                },
              }}
            >
              {saving ? <CircularProgress size={24} color="inherit" /> : 'Save'}
            </Button>
          </Box>
        </Box>
      )}
    </Drawer>
  );
};

